import { useCallback, useState } from 'react';
import { usePlatform } from '../platform/context';
import { buildBackup } from './backup';
import { ZipTooLargeError } from './zip';

/**
 * Export, from wherever it's asked for — the settings page for everything, a
 * board's selection for some of it. Shared because both need the same three
 * states and the same sentences when it goes wrong.
 */

export type ExportState = {
  /** Zipping, then waiting on the save dialog. */
  busy: boolean;
  /** Set once a file has actually been written. */
  saved: string | undefined;
  error: string | undefined;
};

export type Export = ExportState & {
  /** `itemIds` narrows it to a selection. Undefined means everything. */
  run: (itemIds?: readonly string[]) => Promise<void>;
  dismiss: () => void;
};

export function useExport(): Export {
  const platform = usePlatform();
  const [state, setState] = useState<ExportState>({
    busy: false,
    saved: undefined,
    error: undefined,
  });

  const run = useCallback(
    async (itemIds?: readonly string[]) => {
      setState({ busy: true, saved: undefined, error: undefined });

      try {
        const file = await buildBackup(itemIds);
        const written = await platform.saveFile(file.name, file.blob);
        // Dismissing the save dialog is a decision, not a failure. Nothing is
        // reported either way except that it didn't happen.
        setState({ busy: false, saved: written ? file.name : undefined, error: undefined });
      } catch (thrown) {
        setState({ busy: false, saved: undefined, error: explain(thrown) });
      }
    },
    [platform],
  );

  return { ...state, run, dismiss: () => setState({ busy: false, saved: undefined, error: undefined }) };
}

function explain(thrown: unknown): string {
  // No case for `PlatformUnavailableError` any more: every build can write a
  // backup, the browser one through a download. See `platform/web.ts`.
  if (thrown instanceof ZipTooLargeError) return thrown.message;
  // The Rust side's messages are already written to be read by a person.
  if (thrown instanceof Error && thrown.message !== '') return thrown.message;
  return "That backup couldn't be written.";
}
